import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, Video, Link as LinkIcon, ImageIcon, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ICONS = { pdf: FileText, video: Video, link: LinkIcon, image: ImageIcon };

export function ResourcesPanel({ testid = "resources-panel" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", resource_type: "link", url: "", tags: "" });

  const load = () => api.get("/resources").then((r) => setItems(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/resources", { ...form, tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Resource added");
      setForm({ title: "", description: "", resource_type: "link", url: "", tags: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete resource?")) return;
    await api.delete(`/resources/${id}`);
    load();
  };

  return (
    <div className="space-y-8" data-testid={testid}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Library</div>
          <h1 className="font-serif text-5xl mt-3">Resources</h1>
        </div>
        {user?.role === "coach" && (
          <button className="btn-primary" onClick={() => setOpen(!open)} data-testid="new-resource-btn"><Plus size={14}/> New resource</button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="card-luxe space-y-5 max-w-3xl" data-testid="new-resource-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><label className="label-luxe">Title</label><input required className="input-luxe" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title" /></div>
            <div>
              <label className="label-luxe">Type</label>
              <select className="input-luxe" value={form.resource_type} onChange={(e) => setForm({ ...form, resource_type: e.target.value })}>
                {["link", "pdf", "video", "image"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="md:col-span-2"><label className="label-luxe">URL</label><input required className="input-luxe" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="resource-url" /></div>
            <div className="md:col-span-2"><label className="label-luxe">Description</label><textarea rows={3} className="input-luxe" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="md:col-span-2"><label className="label-luxe">Tags (comma)</label><input className="input-luxe" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
          </div>
          <button className="btn-primary" data-testid="resource-submit">Add</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => {
          const Icon = ICONS[r.resource_type] || LinkIcon;
          return (
            <a key={r.resource_id} href={r.url} target="_blank" rel="noreferrer" className="card-luxe block hover:border-luxe-strong" data-testid={`resource-${r.resource_id}`}>
              <div className="flex items-center justify-between">
                <Icon size={18} className="text-accent" />
                <div className="overline">{r.resource_type}</div>
              </div>
              <h3 className="font-serif text-xl mt-3">{r.title}</h3>
              {r.description && <p className="text-secondary-luxe text-sm mt-2 line-clamp-2">{r.description}</p>}
              {(r.tags || []).length > 0 && <div className="mt-3 flex flex-wrap gap-1">
                {r.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-widest text-tertiary-luxe">#{t}</span>)}
              </div>}
              {user?.role === "coach" && (
                <button onClick={(e) => { e.preventDefault(); remove(r.resource_id); }} className="btn-ghost mt-3" data-testid={`del-res-${r.resource_id}`}><Trash2 size={14}/></button>
              )}
            </a>
          );
        })}
        {items.length === 0 && <div className="text-secondary-luxe">No resources yet.</div>}
      </div>
    </div>
  );
}
